import { isServer } from '@/shared/util/ssr';
import type { IEnvironment } from '@tarslib/types/lib/environment';
import cloneDeep from 'lodash-es/cloneDeep';
import { LocalStorageKey } from '../LocalStorageKey';

function getCompatibleLocalStorage() {
  return !isServer
    ? localStorage
    : {
        getItem: () => undefined,
        setItem: () => undefined,
        removeItem: () => undefined,
      };
}

export const loadEnvironmentsMap = () => {
  return getCompatibleLocalStorage().getItem(LocalStorageKey.USE_ENVIRONMENT) || '{}';
};

const saveEnvironmentsMap = (value: string) => {
  try {
    getCompatibleLocalStorage().setItem(LocalStorageKey.USE_ENVIRONMENT, value);
  } catch (error) {
    // ignore
    console.error(error);
  }
};

export const saveEnvironmentsByDocsId = (docsId: number | string, environments: IEnvironment[]) => {
  try {
    const localEnvironmentsMapStr = loadEnvironmentsMap();
    const emptyEnvironmentsMap = !localEnvironmentsMapStr || localEnvironmentsMapStr === '{}';
    let localEnvironmentsMap: Record<
      string | number,
      {
        environmentList?: IEnvironment[];
      }
    > = emptyEnvironmentsMap ? {} : JSON.parse(localEnvironmentsMapStr);
    if (!localEnvironmentsMap) {
      localEnvironmentsMap = {};
    }
    const cachedEnvironments = localEnvironmentsMap[docsId];
    const toPersistEnvironmentIds = new Set<number>();
    const newCachedEnvironments: IEnvironment[] = [];
    environments.forEach((item) => {
      if (toPersistEnvironmentIds.has(item.id)) {
        return;
      }
      toPersistEnvironmentIds.add(item.id);
      (item as unknown as { cachedTime: number }).cachedTime = Date.now();
      newCachedEnvironments.push(item);
    });
    cachedEnvironments?.environmentList?.forEach((item) => {
      if (!item || toPersistEnvironmentIds.has(item.id)) {
        return;
      }
      toPersistEnvironmentIds.add(item.id);
      const cachedTime = (item as unknown as { cachedTime: number }).cachedTime;
      if (cachedTime && cachedTime <= Date.now() - 1000 * 60 * 60 * 24 * 30) {
        // Skip items older than 30 days
        return;
      }
      if (!cachedTime) {
        (item as unknown as { cachedTime: number }).cachedTime = Date.now();
      }
      newCachedEnvironments.push(item);
    });
    localEnvironmentsMap[docsId] = {
      environmentList: newCachedEnvironments,
    };
    saveEnvironmentsMap(JSON.stringify(localEnvironmentsMap));
  } catch (error) {
    // ignore
    console.error(error);
  }
};

export const loadSelectedEnvironmentMap = () => {
  return getCompatibleLocalStorage().getItem(LocalStorageKey.USE_ENVIRONMENT_ID) || '{}';
};

const saveSelectedEnvironmentMap = (value: string) => {
  try {
    getCompatibleLocalStorage().setItem(LocalStorageKey.USE_ENVIRONMENT_ID, value);
  } catch (error) {
    // ignore
    console.error(error);
  }
};

export const saveSelectedEnvironmentIdByDocsId = (
  docsId: number | string,
  selectedEnvironmentId: number,
) => {
  try {
    const localEnvironmentsMapStr = loadSelectedEnvironmentMap();
    const emptyEnvironmentsMap = !localEnvironmentsMapStr || localEnvironmentsMapStr === '{}';
    let localEnvironmentsMap: Record<
      string | number,
      {
        selectedEnvironmentId?: number;
      }
    > = emptyEnvironmentsMap ? {} : JSON.parse(localEnvironmentsMapStr);
    if (!localEnvironmentsMap) {
      localEnvironmentsMap = {};
    }
    localEnvironmentsMap[docsId] = {
      selectedEnvironmentId,
    };
    saveSelectedEnvironmentMap(JSON.stringify(localEnvironmentsMap));
  } catch (error) {
    // ignore
    console.error(error);
  }
};

export const loadSelectedEnvironmentIdByDocsId = (docsId: number | string): number | undefined => {
  try {
    const localEnvironmentsMapStr = loadSelectedEnvironmentMap();
    if (!localEnvironmentsMapStr || localEnvironmentsMapStr === '{}') {
      return undefined;
    }
    const localEnvironmentsMap = JSON.parse(localEnvironmentsMapStr);
    const selectedEnvironment = localEnvironmentsMap[docsId];
    return selectedEnvironment ? selectedEnvironment.selectedEnvironmentId : undefined;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};
